// src/pages/Login/Login.tsx
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import api from 'services/api/config/api';
import { GoogleOAuthProvider, GoogleLogin, CredentialResponse } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';
import { ForgotPasswordModal } from 'components';
import { useCognito } from 'common/hooks';
import { usePostLogin } from 'services/hooks/mutations';
import { Button, Checkbox, Input } from 'common/components';
import { assetPath } from 'utils/getBasePath';

import styles from './Login.module.scss';

interface LoginFormData {
    email: string;
    password: string;
    consent: boolean;
}

const Login = () => {
    const navigate = useNavigate();

    // React Hook Form setup
    const {
        register,
        handleSubmit,
        control,
        formState: { errors, isSubmitting },
    } = useForm<LoginFormData>({
        defaultValues: {
            email: '',
            password: '',
            consent: false,
        },
        mode: 'onBlur',
    });

    // Other state
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isForgotPasswordModalOpen, setIsForgotPasswordModalOpen] = useState(false);
    // const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
    // Google client ID
    const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

    // Cognito hook
    const { authenticateUser } = useCognito();
    const { postLogin, isPending } = usePostLogin();

    // Updated login function with direct Cognito authentication
    const handleLogin = async (data: LoginFormData) => {
        setError(null);
        setIsLoading(true);

        const email = data.email.trim().toLowerCase();
        try {
            authenticateUser(email, data.password, {
                onSuccess: async function (result) {
                    const idToken = result.getIdToken().getJwtToken();

                    await postLogin(idToken, {
                        onSuccess: ({ userRole }) => {
                            localStorage.setItem('userRole', userRole);
                            navigate('/dashboard');
                        },
                        onError: (error) => {
                            console.error('Login failed:', error);
                            setError(error.message || 'Login failed');
                            setIsLoading(false);
                        },
                    });
                },

                onFailure: function (err) {
                    console.error('Cognito authentication error:', err);
                    setError(err.message || 'Authentication failed');
                    setIsLoading(false);
                },

                newPasswordRequired: function (_userAttributes, _requiredAttributes) {
                    setError("You need to change your password. Please use the 'Forgot Password' option.");
                    setIsLoading(false);
                },
            });
        } catch (err) {
            console.error('Login error:', err);
            setError((err as { message: string })?.message || 'Login failed');
            setIsLoading(false);
        }
    };

    // Updated handleGoogleSuccess function that works with Identity Pool
    const handleGoogleSuccess = async (credentialResponse: CredentialResponse) => {
        setIsLoading(true);
        setError(null);

        try {
            // Decode the JWT token from Google
            const decodedToken = jwtDecode<{ email: string; name: string; sub: string }>(
                credentialResponse.credential || ''
            );
            const { email, name, sub } = decodedToken;

            // Send the Google token to your backend for authentication through Identity Pool
            const response = await api.post('/auth/google-login', {
                googleToken: credentialResponse.credential,
                email,
                name,
                googleId: sub,
            });

            // Handle successful login
            const { token, tenantId, userRole } = response.data;

            localStorage.setItem('userRole', userRole || '');

            navigate('/dashboard');
        } catch (err) {
            console.error('Google login error:', err);
            setError(
                (err as { response?: { data?: { error: string } }; message: string })?.response?.data?.error ||
                    (err as { message: string })?.message ||
                    'Google login failed'
            );
        } finally {
            setIsLoading(false);
        }
    };

    // Handle Google login error
    const handleGoogleError = () => {
        setError('Google login failed. Please try again.');
        setIsLoading(false);
    };

    const handleDemoClick = () => {
        navigate('/demo-request');
    };
    const logoUrl = assetPath('/SLAILOGO.png');

    return (
        <GoogleOAuthProvider clientId={googleClientId}>
            <div className={styles.loginPageContainer}>
                <div className={styles.loginBox}>
                    {/* Logo & Tagline */}
                    <div className={styles.loginLogoRow}>
                        <img src={logoUrl} alt='SLAI Logo' className={styles.slaiLogo} />
                        <h2 className={styles.slaiText}>straightline.ai</h2>
                    </div>
                    <p className={styles.demoText}>Need access? Contact your administrator for access.</p>

                    {/* Regular login form - remove the extra container div */}
                    <form onSubmit={handleSubmit(handleLogin)} className={styles.loginForm}>
                        <Input
                            {...register('email', {
                                required: 'Email is required',
                                pattern: {
                                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                    message: 'Invalid email address',
                                },
                            })}
                            label='Email'
                            type='email'
                            placeholder='Enter your email address'
                            disabled={isLoading || isSubmitting}
                            error={!!errors.email}
                            errorText={errors.email?.message}
                        />

                        <div>
                            <Input
                                {...register('password', {
                                    required: 'Password is required',
                                    minLength: {
                                        value: 6,
                                        message: 'Password must be at least 6 characters',
                                    },
                                })}
                                label='Password'
                                type='password'
                                placeholder='Enter your Password'
                                disabled={isLoading || isSubmitting}
                                error={!!errors.password}
                                errorText={errors.password?.message}
                            />
                            <div className={styles.forgotRow}>
                                <Button
                                    type='button'
                                    variant='link'
                                    onClick={() => setIsForgotPasswordModalOpen(true)}
                                    disabled={isLoading || isSubmitting}
                                >
                                    Forgot my password
                                </Button>
                            </div>
                        </div>

                        {error && <p className={styles.loginError}>{error}</p>}

                        <Controller
                            control={control}
                            name='consent'
                            render={({ field }) => (
                                <Checkbox
                                    label='By checking this box, you consent to receive SMS messages from. Reply STOP to cancel, HELP for help. Msg & data rates may apply'
                                    checked={field.value}
                                    onChange={field.onChange}
                                    onBlur={field.onBlur}
                                    error={!!errors.consent}
                                    errorText={errors.consent?.message as string | undefined}
                                />
                            )}
                        />

                        <Button
                            type='submit'
                            variant='primary'
                            disabled={isLoading || isSubmitting}
                            size='md'
                            style={{ marginTop: 'var(--margin-lg)' }}
                        >
                            {isLoading || isSubmitting ? 'Logging in...' : 'Log in'}
                        </Button>

                        {/* TODO: Uncomment this when we have a demo request feature enabled */}
                        {/* <Button variant='ghost' onClick={handleDemoClick}>
                            Want a Demo?
                        </Button> */}
                    </form>

                    {/* Footer */}
                    <div className={styles.loginFooter}>
                        <p>©{new Date().getFullYear()} SLAI, Inc. All Rights Reserved.</p>
                        <div className='flex gap-lg justify-center mt-sm'>
                            <Button variant='linkText' size='sm' onClick={() => navigate('/privacy')}>
                                Privacy Policy
                            </Button>

                            <Button variant='linkText' size='sm' onClick={() => navigate('/terms')}>
                                Terms of Service
                            </Button>
                            {/* <a href='#privacy'>Privacy Policy</a>, <a href='#cookies'>Manage Cookies</a>,{' '}
                            <a href='#choices'>Your Privacy Choices</a> */}
                        </div>
                    </div>
                </div>
            </div>

            {/* Forgot Password Modal */}
            <ForgotPasswordModal
                isOpen={isForgotPasswordModalOpen}
                onClose={() => setIsForgotPasswordModalOpen(false)}
            />
        </GoogleOAuthProvider>
    );
};

export default Login;
